import { useState } from 'react';
import { motion } from 'motion/react';
import { X, Download, Share2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCapturesStore, type Capture } from '@/stores/captures-store';
import { useUIStore } from '@/stores/ui-store';
import { formatDistanceToNow } from '@/lib/utils';

export default function CaptureViewer() {
  const { captures, selectedCapture, setSelectedCapture, deleteCapture, setViewerOpen } =
    useCapturesStore();
  const { showToast } = useUIStore();
  const [isZoomed, setIsZoomed] = useState(false);

  if (!selectedCapture) return null;

  const currentIndex = captures.findIndex((c) => c.id === selectedCapture.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < captures.length - 1;

  const goToPrev = () => {
    if (hasPrev) {
      setSelectedCapture(captures[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (hasNext) {
      setSelectedCapture(captures[currentIndex + 1]);
    }
  };

  const handleClose = () => {
    setSelectedCapture(null);
    setViewerOpen(false);
  };

  const downloadCapture = async () => {
    try {
      const link = document.createElement('a');
      link.href = selectedCapture.dataUrl;
      link.download = `visionar-${selectedCapture.type}-${Date.now()}.${
        selectedCapture.type === 'photo' ? 'jpg' : 'mp4'
      }`;
      link.click();
      showToast('Download started', 'success');
    } catch {
      showToast('Failed to download', 'error');
    }
  };

  const shareCapture = async () => {
    try {
      if (navigator.share) {
        const response = await fetch(selectedCapture.dataUrl);
        const blob = await response.blob();
        const file = new File(
          [blob],
          `visionar-${selectedCapture.type}.${selectedCapture.type === 'photo' ? 'jpg' : 'mp4'}`,
          { type: selectedCapture.type === 'photo' ? 'image/jpeg' : 'video/mp4' }
        );

        await navigator.share({
          files: [file],
          title: 'VisionAR Capture',
          text: selectedCapture.modelName
            ? `Check out ${selectedCapture.modelName} in AR!`
            : 'Check out my AR capture!',
        });
      } else {
        showToast('Sharing not supported on this device', 'info');
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        showToast('Failed to share', 'error');
      }
    }
  };

  const handleDelete = () => {
    deleteCapture(selectedCapture.id);
    showToast('Capture deleted', 'success');

    // Navigate to next or prev capture, or close
    if (hasNext) {
      setSelectedCapture(captures[currentIndex + 1]);
    } else if (hasPrev) {
      setSelectedCapture(captures[currentIndex - 1]);
    } else {
      handleClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] bg-black"
    >
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="absolute top-0 left-0 right-0 z-10 safe-area-top"
      >
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleClose}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>

          <div className="text-center">
            <p className="text-sm font-semibold">
              {currentIndex + 1} / {captures.length}
            </p>
            {selectedCapture.modelName && (
              <p className="text-xs text-white/60">{selectedCapture.modelName}</p>
            )}
          </div>

          <div className="w-11" /> {/* Spacer for centering */}
        </div>
      </motion.div>

      {/* Media */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        onClick={() => setIsZoomed(!isZoomed)}
      >
        {selectedCapture.type === 'photo' ? (
          <motion.img
            key={selectedCapture.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: isZoomed ? 1.5 : 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            src={selectedCapture.dataUrl}
            alt="Capture"
            className={`max-w-full max-h-full object-contain transition-transform duration-200 ${
              isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
            }`}
          />
        ) : (
          <motion.video
            key={selectedCapture.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            src={selectedCapture.dataUrl}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-full"
          />
        )}
      </div>

      {/* Navigation arrows */}
      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrev();
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Footer */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="absolute bottom-0 left-0 right-0 z-10 safe-area-bottom"
      >
        <div className="p-4">
          {/* Info */}
          <div className="text-center mb-4">
            <p className="text-sm text-white/60">
              {formatDistanceToNow(new Date(selectedCapture.createdAt))}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <button
              onClick={downloadCapture}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Download size={22} />
              </div>
              <span className="text-xs text-white/70">Download</span>
            </button>

            <button
              onClick={shareCapture}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Share2 size={22} />
              </div>
              <span className="text-xs text-white/70">Share</span>
            </button>

            <button
              onClick={handleDelete}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-red-500/10 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <Trash2 size={22} className="text-red-400" />
              </div>
              <span className="text-xs text-red-400">Delete</span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
